import { formatDate } from '@frontend/mwm-ui'
import { useState } from 'react'
import DatePicker from 'react-datepicker'
import { MdArrowBack, MdArrowForward } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { IDailyCatalogPost } from '~/types/catalog'

import { DailyPost } from './DailyPost'

import CatalogTemplatePreviewModal from '../CatalogTemplatePreviewModal'

import Button from '~/components/Buttons/Button'
import AddToModal from '~/components/CatalogModal'
import Filters, { FiltersContent } from '~/components/Filters'
import DateInput from '~/components/Inputs/Basic/DateInput'
import ViewTitle from '~/components/ViewTitle'
import { MAX_DAYS_RANGE } from '~/const'
import { EVENTS } from '~/const/catalog'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useDailyPostsQuery } from '~/queries/catalog'
import { getDatesBetween } from '~/utils/date'
import dayjs from '~/utils/dayjs'

import './styles.scss'

const Daily = (): ReactElement => {
  /** Local state */

  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'))

  const [endDate, setEndDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'))

  const [isAddToModalOpen, setIsAddToModalOpen] = useState(false)

  const [selectedModal, setSelectedModal] = useState<'category' | 'daily'>('category')

  const [selectedTemplate, setSelectedTemplate] = useState('')

  const [selectedTemplatePreviewUrl, setSelectedTemplatePreviewUrl] = useState('')

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

  const daysInSelectedRange = getDatesBetween(startDate, endDate)

  /**
   * It takes an array of posts and an array of "YYYY-MM-DD" dates, arranges the dates on a monthly calendar, and to each day, assigns a DailyPost component.
   * The monthly calendar has 42 possible positions (6 weeks * 7 days). The week starts on Monday.
   * @return an array of grid items (ReactElement)
   */
  const getGridItems = (posts: IDailyCatalogPost[], daysInSelectedRange: string[]): ReactElement[] => {
    const firstDate = dayjs(daysInSelectedRange[0], 'YYYY-MM-DD')
    const startDayOfWeek = (firstDate.day() + 6) % 7
    const gridItems = Array(42).fill(null)

    daysInSelectedRange.forEach((day, idx) => {
      const dayIndex = (startDayOfWeek + idx) % 7
      const weekIndex = Math.floor((startDayOfWeek + idx) / 7)
      const postOnThatDay = posts?.find((el) => day === el.day)

      gridItems[weekIndex * 7 + dayIndex] = (
        <DailyPost
          cleanedPrompt={postOnThatDay?.cleaned_prompt}
          coloringTemplateId={postOnThatDay?.coloring_template_id}
          day={day}
          eventTooltipPlacement={dayIndex === 0 ? 'topLeft' : dayIndex === 6 ? 'topRight' : 'top'}
          events={EVENTS.filter(({ date }) => date === day).map(({ holidayName }) => holidayName)}
          key={idx}
          listDailyPostParams={{
            endDate,
            startDate,
          }}
          onOpenAddToCategoryModal={(templateId) => handleOpenAddToModal(templateId, 'category')}
          onOpenAddToDailyModal={(templateId) => handleOpenAddToModal(templateId, 'daily')}
          onOpenPreview={(mediaUrl) => handleOpenPreview(mediaUrl)}
          originType={postOnThatDay?.origin_type}
          previewFileId={postOnThatDay?.preview_png_file_id}
        />
      )
    })

    return gridItems
  }

  /** Queries & Mutations */

  const { data: dailyPostsData = [] } = useDailyPostsQuery({ endDate, startDate })

  /** Effects */

  useDocumentTitle('Catalog - Daily')

  /** Handlers */

  const handleMonthYearPickerChange = (date: Date | null) => {
    setStartDate(dayjs(date).startOf('month').format('YYYY-MM-DD'))
    setEndDate(dayjs(date).endOf('month').format('YYYY-MM-DD'))
  }

  const handleArrowButtonClick = (action: 'previous' | 'next') => () => {
    const referenceDate =
      action === 'previous'
        ? dayjs(startDate).startOf('month').subtract(1, 'month')
        : dayjs(startDate).startOf('month').add(1, 'month')

    setStartDate(referenceDate.format('YYYY-MM-DD'))
    setEndDate(referenceDate.endOf('month').format('YYYY-MM-DD'))
  }

  const handleOpenPreview = (mediaUrl: string) => {
    setSelectedTemplatePreviewUrl(mediaUrl)
    setIsPreviewModalOpen(true)
  }

  const handleOpenAddToModal = (templateId: string, type: 'daily' | 'category') => {
    setIsAddToModalOpen(true)
    setSelectedModal(type)
    setSelectedTemplate(templateId)
  }

  const handleCloseAddToModal = () => setIsAddToModalOpen(false)

  const handlePreviewModalState = (isOpen: boolean) => () => setIsPreviewModalOpen(isOpen)

  /** Render */

  return (
    <div className="catalog-daily-view">
      <ViewTitle className="catalog-daily-view__title">Daily</ViewTitle>
      <div className="catalog-daily-view__content">
        <Filters className="catalog-daily-filters">
          <FiltersContent>
            <div className="month-controls">
              <Button ghost onClick={handleArrowButtonClick('previous')}>
                <MdArrowBack color="#ff5301" size={16} />
              </Button>
              <DatePicker
                className="month-picker"
                customInput={
                  <Button className="month-picker-wrapper__selected-month" ghost>
                    {formatDate(startDate, 'MMMM')}
                  </Button>
                }
                holidays={EVENTS}
                onChange={handleMonthYearPickerChange}
                selected={new Date(startDate)}
                showMonthYearPicker
              />
              <Button ghost onClick={handleArrowButtonClick('next')}>
                <MdArrowForward color="#ff5301" size={16} />
              </Button>
            </div>
            <DateInput
              calendarStartDay={1}
              endDate={endDate}
              holidays={EVENTS}
              label="Custom date range"
              maxRangeDays={MAX_DAYS_RANGE}
              onDateRangeChange={(value: unknown) => {
                const [start, end] = value as string[]
                setStartDate(formatDate(start, 'YYYY-MM-DD'))
                setEndDate(formatDate(end, 'YYYY-MM-DD'))
              }}
              startDate={startDate}
              withRange
            />
          </FiltersContent>
        </Filters>
        <div className="posts">
          {getGridItems(dailyPostsData, daysInSelectedRange).map((el, idx) => (
            <div key={idx}>{el}</div>
          ))}
        </div>
      </div>
      <CatalogTemplatePreviewModal
        isOpen={isPreviewModalOpen}
        mediaUrl={selectedTemplatePreviewUrl}
        onClose={handlePreviewModalState(false)}
      />
      <AddToModal
        isOpen={isAddToModalOpen}
        onClose={handleCloseAddToModal}
        selectedTemplate={selectedTemplate}
        type={selectedModal}
      />
    </div>
  )
}

export default Daily
